import {useQuery, UseQueryResult} from '@tanstack/react-query';
import ProblemDetailsError from '../ProblemDetailsError.ts';
import {useCreateAuthenticatedFetch} from '../useCreateAuthenticatedFetch.ts';
import baseUrl from '../baseUrl.ts';
import {handleResponse} from '../handleResponse.ts';
import useVoiceAssistantAvailability from '../../voice/useVoiceAssistantAvailability.ts';

// Mirrors GptAssistant/Voice/VoiceCapabilityModels.cs (camelCase on the wire).

export interface CapabilityFeature {
    id: string;
    label: string;
    description?: string | null;
    spokenVerb: string;
}

export interface CapabilityConnector {
    id: string;
    displayName: string;
    description?: string | null;
}

export interface CapabilityMcpServer {
    label: string;
    displayName: string;
    description?: string | null;
    toolNames?: string[] | null;
    toolCount?: number | null;
    requiresApproval: boolean;
}

export interface CapabilitySkill {
    name: string;
    description?: string | null;
}

export interface CapabilityPrompt {
    buttonText: string;
}

export interface AssistantVoiceCapabilities {
    assistantId: string;
    name: string;
    description?: string | null;
    userGuideExcerpt?: string | null;
    modelLabel?: string | null;
    features: CapabilityFeature[];
    connectors: CapabilityConnector[];
    mcpServers: CapabilityMcpServer[];
    skills: CapabilitySkill[];
    starterPrompts: CapabilityPrompt[];
    knowledgeFileCount: number;
    unavailableReason?: string | null;
    /** Short prose for the voice prompt, at most 600 characters. */
    spokenSummary: string;
}

export const assistantVoiceCapabilitiesQueryKey = (
    assistantId: string | null | undefined
) => ['assistantVoiceCapabilities', assistantId] as const;

/** Server profiles are cached for a few minutes too; this matches that window. */
export const assistantVoiceCapabilitiesStaleTime = 5 * 60_000;

export const fetchAssistantVoiceCapabilities = async (
    authenticatedFetch: ReturnType<typeof useCreateAuthenticatedFetch>,
    assistantId: string
): Promise<AssistantVoiceCapabilities> => {
    const response = await authenticatedFetch(
        `${baseUrl}/api/assistants/${assistantId}/voice-capabilities`
    );
    return handleResponse(response);
};

/**
 * What the open assistant can do, resolved server-side the way a run resolves
 * its tools. Only fetched when voice is available to this user: the endpoint
 * is gated on the voice feature and nothing else reads it.
 */
export const useAssistantVoiceCapabilities = (
    assistantId: string | null | undefined
): UseQueryResult<AssistantVoiceCapabilities, Error | ProblemDetailsError> => {
    const authenticatedFetch = useCreateAuthenticatedFetch();
    const {voiceAssistantEnabled} = useVoiceAssistantAvailability();

    return useQuery({
        queryKey: assistantVoiceCapabilitiesQueryKey(assistantId),
        queryFn: () =>
            fetchAssistantVoiceCapabilities(authenticatedFetch, assistantId!),
        enabled: !!assistantId && voiceAssistantEnabled,
        staleTime: assistantVoiceCapabilitiesStaleTime,
        retry: false
    });
};
